import * as React from "react";
import * as ReactDOM from "react-dom";
import { getUserGroups } from "../../../api/userGroups";
import { IUserGroup } from "../../../models/get";
import { Loader, LoaderStyle } from "../../../components/loader";
import { ToolBar, ToolBarItem, ToolBarSeparator } from "../../../components/toolbar";
import { InputSize } from "../../../components/input";
import { TextBox } from "../../../components/textBox";
import { Button, ButtonType, ButtonSize } from "../../../components/button";
import { Table, ITableColumn, ITableRowEventArgs } from "../../../components/table";
import SettingsNav from "../nav";
import Dialog from "./dialog";

interface IProperties
{
}

interface IState
{
    /**
     * Gibt an, ob gerade geladen wird.
     */
    isLoading?: boolean,

    /**
     * Gibt an, ob der Dialog geöffnet ist.
     */
    isDialogOpen?: boolean,

    /**
     * Gibt an, ob der Dialog zum bearbeiten eines Datensatzes geöffnet wird.
     */
    isDialogInEditMode?: boolean,

    /**
     * Enthält Text der zum Filtern der Liste verwendet wird.
     */
    filterValue?: string,

    /**
     * Enthält die geladenen Daten.
     */
    userGroups?: IUserGroup[],

    /**
     * Enthält die gefilterten Daten.
     */
    filteredUserGroups?: IUserGroup[],

    /**
     * Enthält den momentan selektierten Datensatz.
     */
    selectedItem?: IUserGroup,

    /**
     * Enthält den momentan selektierten Zeilenindex.
     */
    selectedRowIndex?: string
}

export class UsergroupSettings extends React.Component<IProperties, IState>
{
    /**
     * Erstellt eine neue Instanz der Klasse @see UserGroupsList.
     * @param props Alle gesetzten Eigenschaften.
     */
    constructor(props: IProperties)
    {
        super(props);

        this.state = {
            isLoading: true
        }

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        var userGroups = await getUserGroups();

        this.setState({
            isLoading: false,
            userGroups: userGroups,
            filteredUserGroups: userGroups
        });
    }

    /**
     * Aktualisiert die Liste.
     */
    private async refreshAsync()
    {
        this.setState({
            isLoading: true,
            filterValue: null
        });

        await this.initAsync(this.props);
    }

    /**
     * Wird ausgeführt, sobald sich der Text der Filter TextBox ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     */
    private onFilterTextChanged = (value: any[], originalValue: string[]): void =>
    {
        const { userGroups } = this.state;
        var filter: string = value[0];
        var filteredData: IUserGroup[] = [];

        if (filter != null && filter !== "")
            filteredData = userGroups.where(s => (s.name && s.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0));
        else
            filteredData = userGroups;

        this.setState({
            filterValue: value[0],
            filteredUserGroups: filteredData
        });
    }

    /**
     * Wird ausgeführt sobald auf eine Zeile geklickt wird. Die Daten der ausgewählten
     * Zeile werden hier für weitere Zwecke zwischengespeichert.
     * @param e
     */
    private onRowClick = (e: ITableRowEventArgs<any>) =>
    {
        this.setState({
            selectedItem: e.dataSource,
            selectedRowIndex: e.index
        });
    }

    /**
     * Öffnet den Dialog zum erstellen eines neuen Datensatzes.
     */
    private onNewClick = () =>
    {
        this.setState({
            isDialogOpen: true,
            isDialogInEditMode: false
        });
    }

    /**
     * Öffnet den Dialog zum bearbeiten eines Datensatzes.
     */
    private onEditClick = () =>
    {
        this.setState({
            isDialogOpen: true,
            isDialogInEditMode: true
        });
    }

    /**
     * Wird aufgerufen, sobald sich der Dialog schließt.
     * @param refresh Angabe, ob die Ansicht aktualisiert werden soll.
     */
    private onDialogClosed = async (refresh: boolean) =>
    {
        if (refresh)
        {
            await this.refreshAsync();
            this.setState({
                isDialogOpen: false,
                selectedItem: null,
                selectedRowIndex: null
            });
        }
        else
        {
            this.setState({
                isDialogOpen: false
            });
        }
    }

    /**
     * Render einen InfoText wenn die Liste leer ist.
     */
    private renderInfoText(): JSX.Element
    {
        return (
            <div className="settings-default_empty">
                <i className="settings-default_empty_icon mdi mdi-account-group"></i>
                <div className="settings-default_empty_header">Benutzergruppen</div>
                <div
                    style={{
                        width: 370
                    }}
                    className="settings-default_empty_text">
                    Erstellen Sie Benutzergruppen um Berechtigungen zu vergeben.
                </div>
                <Button
                    icon="plus"
                    text="Benutzergruppe"
                    type={ButtonType.primary}
                    size={ButtonSize.big}
                    onClick={this.onNewClick} />
            </div>
        );
    }

    /**
     * Rendert die Tabelle.
     */
    private renderTable(): JSX.Element
    {
        const { userGroups, filteredUserGroups } = this.state;
        const columns: ITableColumn[] = [
            {
                header: "Name",
                accessor: "name",
                width: 200
            }
        ];

        if (userGroups != null && userGroups.length > 0)
        {
            return (
                <Table
                    style={{
                        position: "relative"
                    }}
                    dataSource={filteredUserGroups}
                    columns={columns}
                    keyAccessor="id"
                    onRowClick={this.onRowClick} />
            );
        }
        else
        {
            return this.renderInfoText();
        }
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const { isLoading, isDialogOpen, isDialogInEditMode, filterValue, userGroups, selectedItem } = this.state;
        const isListEmpty: boolean = userGroups == null || userGroups.length === 0;
        const isItemSelected: boolean = selectedItem != null;
        const isAdminGroup: boolean = selectedItem && selectedItem.name.toLowerCase() === "administrator";
        
        if (isLoading)
        {
            return (
                <Loader
                    style={{ marginTop: 40 }}
                    loaderStyle={LoaderStyle.style1}
                    infoText="Benutzergruppen werden geladen" />
            );
        }
        
        return (
            <div className="settings-content">
                <div className="settings-content_container">
                    <div className="settings-content_nav">
                        <SettingsNav selected="usergroups" />
                    </div>
                    <div className="settings-default-container">
                        {
                            !isListEmpty &&
                            <div className="settings-default_toolbar">
                                <ToolBar hasTopBorder={false}>
                                    <ToolBarItem
                                        icon="plus"
                                        text="Neu"
                                        onClick={this.onNewClick} />
                                    <ToolBarSeparator />
                                    <ToolBarItem
                                        icon="pencil"
                                        text="Bearbeiten"
                                        onClick={this.onEditClick}
                                        isDisabled={!isItemSelected || isAdminGroup} />
                                </ToolBar>
                            </div>
                        }
                        {
                            !isListEmpty &&
                            <div className="settings-default_filter">
                                <TextBox
                                    placeholder="Filter Name..."
                                    size={InputSize.normal}
                                    value={filterValue}
                                    onTextChange={this.onFilterTextChanged} />
                            </div>
                        }
                        <div className="settings-default">
                            {this.renderTable()}
                        </div>
                        {
                            isDialogOpen &&
                            <Dialog
                                userGroupId={isDialogInEditMode && isItemSelected && selectedItem.id}
                                onClose={this.onDialogClosed} />
                        }
                        </div>
                </div>
            </div>
        );
    }
}